/**
 * TrendingAgentsBoard
 * Ranked table of agents with sparklines and delta badges.
 * Columns: rank, agent name, tenant, conversations, users, tokens,
 *          sparkline, and three Δ% badges (convos, users, tokens).
 */

import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Select, Skeleton, Tooltip, Typography, theme } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { AgentStat, TimeseriesPoint, TenantNode } from '../../types/insights';
import { useChartTheme, PALETTE } from '../../utils/chartTheme';
import {
  renderAgentOption,
  renderTenantOption,
  agentFilterOption,
  AgentPlaceholder,
  TenantPlaceholder,
  AGENT_SELECT_STYLE,
  TENANT_SELECT_STYLE,
  AGENT_DROPDOWN_STYLE,
  TENANT_DROPDOWN_STYLE,
} from '../../utils/selectRenderers';

const { Text } = Typography;

const BLUE = '#3b82f6';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}



// ─── Delta badge ──────────────────────────────────────────────────────────────

interface BadgeTheme {
  mutedText: string;
  positiveBg: string;
  positiveText: string;
  negativeBg: string;
  negativeText: string;
  rankBadgeBg: string;
  rankBadgeText: string;
}

function DeltaBadge({ pct, bt }: { pct: number | null; bt: BadgeTheme }) {
  const { t } = useLanguage();
  if (pct === null || (pct !== 999 && Math.abs(pct) < 1)) {
    return (
      <span style={{ fontSize: 11, color: bt.mutedText, fontWeight: 500, whiteSpace: 'nowrap' }}>—</span>
    );
  }
  if (pct === 999) {
    return (
      <span style={{ fontSize: 11, fontWeight: 600, color: bt.positiveText, whiteSpace: 'nowrap' }}>
        {t('insights.pulse.newBadge')}
      </span>
    );
  }
  const positive = pct >= 0;
  const display = Math.abs(pct) >= 10 ? Math.round(Math.abs(pct)) : Math.abs(pct).toFixed(1);
  return (
    <span style={{ fontSize: 11, fontWeight: 600, color: positive ? bt.positiveText : bt.negativeText, whiteSpace: 'nowrap' }}>
      {positive ? '↑' : '↓'} {display}%
    </span>
  );
}

// ─── Sparkline ────────────────────────────────────────────────────────────────

function Sparkline({ data, color = BLUE }: { data: number[]; color?: string }) {
  const W = 60;
  const H = 24;

  if (data.length < 2) {
    return <div style={{ width: W, height: H }} />;
  }

  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (W - 2) + 1;
    const y = H - 2 - ((v / max) * (H - 4)) + 1;
    return `${x},${y}`;
  });

  return (
    <svg width={W} height={H} style={{ display: 'block', overflow: 'visible' }}>
      <polyline
        points={pts.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ─── Rank badge ───────────────────────────────────────────────────────────────

function RankBadge({ rank, bt }: { rank: number; bt: BadgeTheme }) {
  if (rank === 1) return <span style={{ fontSize: 16 }}>🥇</span>;
  if (rank === 2) return <span style={{ fontSize: 16 }}>🥈</span>;
  if (rank === 3) return <span style={{ fontSize: 16 }}>🥉</span>;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        borderRadius: '50%',
        background: bt.rankBadgeBg,
        color: bt.rankBadgeText,
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {rank}
    </span>
  );
}

// ─── Agent row data ───────────────────────────────────────────────────────────

interface AgentRow {
  chatbot_id: string;
  chatbot_name: string;
  tenant_id: string;
  tenant_name: string;
  conversations: number;
  messages: number;
  users: number;
  input_tokens: number;
  output_tokens: number;
  cached_tokens: number;
  cost_usd: number;
  sparklineData: number[];
  delta_conversations: number | null;
  delta_users: number | null;
  delta_tokens: number | null;
}

type SortField = 'conversations' | 'messages' | 'users' | 'input_tokens' | 'output_tokens' | 'cached_tokens' | 'cost_usd'
               | 'delta_conversations' | 'delta_users' | 'delta_tokens';

function buildAgentRows(
  agentStats: AgentStat[],
  agentTimeseries: TimeseriesPoint[]
): AgentRow[] {
  const allDates = Array.from(new Set(agentTimeseries.map(p => p.date))).sort();
  const mid = Math.floor(allDates.length / 2);
  const firstDates = new Set(allDates.slice(0, mid));
  const secondDates = new Set(allDates.slice(mid));

  // Key timeseries by chatbot_id + tenant_id
  const byKey = new Map<string, TimeseriesPoint[]>();
  for (const pt of agentTimeseries) {
    if (!pt.chatbot_id) continue;
    const key = `${pt.chatbot_id}::${pt.tenant_id ?? ''}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key)!.push(pt);
  }

  return agentStats.map(stat => {
    const key = `${stat.chatbot_id}::${stat.tenant_id}`;
    const botPoints = byKey.get(key) ?? [];
    const sorted = [...botPoints].sort((a, b) => a.date.localeCompare(b.date));

    const sparklineData = sorted.slice(-10).map(p => p.conversations);

    const pct = (first: number, second: number, newThreshold = 5): number | null => {
      if (first === 0 && second === 0) return null;
      if (first === 0 && second >= newThreshold) return 999;
      if (first === 0 && second > 0) return null;
      if (second === 0 && first > 0) return -100;
      return ((second - first) / first) * 100;
    };

    const sumField = (pts: TimeseriesPoint[], field: keyof TimeseriesPoint) =>
      pts.reduce((acc, p) => acc + (Number(p[field]) || 0), 0);

    const firstPts = botPoints.filter(p => firstDates.has(p.date));
    const secondPts = botPoints.filter(p => secondDates.has(p.date));

    return {
      chatbot_id: stat.chatbot_id,
      chatbot_name: stat.chatbot_name,
      tenant_id: stat.tenant_id,
      tenant_name: stat.tenant_name,
      conversations: stat.total_conversations,
      messages: stat.total_messages,
      users: stat.active_users,
      input_tokens: stat.total_input_tokens,
      output_tokens: stat.total_output_tokens,
      cached_tokens: stat.total_cached_tokens,
      cost_usd: stat.total_cost_usd,
      sparklineData,
      delta_conversations: pct(sumField(firstPts, 'conversations'), sumField(secondPts, 'conversations')),
      delta_users: pct(sumField(firstPts, 'users'), sumField(secondPts, 'users')),
      delta_tokens: pct(sumField(firstPts, 'input_tokens'), sumField(secondPts, 'input_tokens')),
    };
  });
}

// Sort delta fields: 999 ("New") is highest positive, null sorts last
function sortDelta(a: number | null, b: number | null, asc: boolean): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const diff = a - b;
  return asc ? diff : -diff;
}

// ─── Props & component ────────────────────────────────────────────────────────

export interface TrendingAgentsBoardProps {
  agentStats: AgentStat[];
  agentTimeseries: TimeseriesPoint[];
  tenantTree?: TenantNode[];
  loading: boolean;
}

export function TrendingAgentsBoard({ agentStats, agentTimeseries, tenantTree = [], loading }: TrendingAgentsBoardProps) {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const ct = useChartTheme();

  const [selectedAgents, setSelectedAgents] = useState<string[]>([]);
  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('conversations');
  const [sortAsc, setSortAsc] = useState(false);

  // Reset local filters when upstream data changes (global scope filter applied)
  useEffect(() => {
    setSelectedAgents([]);
    setSelectedTenants([]);
  }, [agentStats]);

  // Group by name so shared agents appear once
  const agentOptions = useMemo(() => {
    const byName = new Map<string, string[]>();
    for (const a of agentStats) {
      const ids = byName.get(a.chatbot_name) || [];
      if (!ids.includes(a.chatbot_id)) ids.push(a.chatbot_id);
      byName.set(a.chatbot_name, ids);
    }
    return Array.from(byName.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, ids]) => ({ value: ids.join(','), label: name }));
  }, [agentStats]);

  const tenantOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const a of agentStats) {
      if (a.tenant_id && !seen.has(a.tenant_id)) seen.set(a.tenant_id, a.tenant_name);
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ value: id, label: name }));
  }, [agentStats]);

  const rows = useMemo(() => {
    const all = buildAgentRows(agentStats, agentTimeseries);
    let filtered = all;
    if (selectedAgents.length > 0) {
      const ids = new Set(selectedAgents.flatMap(id => id.split(',')));
      filtered = filtered.filter(r => ids.has(r.chatbot_id));
    }
    if (selectedTenants.length > 0) {
      const ids = new Set(selectedTenants);
      filtered = filtered.filter(r => ids.has(r.tenant_id));
    }
    return [...filtered].sort((a, b) => {
      if (sortField === 'delta_conversations') return sortDelta(a.delta_conversations, b.delta_conversations, sortAsc);
      if (sortField === 'delta_users') return sortDelta(a.delta_users, b.delta_users, sortAsc);
      if (sortField === 'delta_tokens') return sortDelta(a.delta_tokens, b.delta_tokens, sortAsc);
      const diff = (a[sortField] as number) - (b[sortField] as number);
      return sortAsc ? diff : -diff;
    });
  }, [agentStats, agentTimeseries, selectedAgents, selectedTenants, sortField, sortAsc]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(prev => !prev);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
  };

  const thStyle: React.CSSProperties = {
    padding: '8px 12px',
    fontSize: 11,
    fontWeight: 700,
    color: ct.tableHeaderText,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    background: ct.tableHeaderBg,
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
    whiteSpace: 'nowrap',
    textAlign: 'center',
  };

  const SortHeader = ({ field, children, tip }: { field: SortField; children: React.ReactNode; tip?: string }) => (
    <th
      onClick={() => handleSort(field)}
      style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
    >
      {children}
      {tip && (
        <Tooltip title={tip}>
          <InfoCircleOutlined style={{ marginInlineStart: 4, fontSize: 10, color: ct.mutedText, cursor: 'help' }} />
        </Tooltip>
      )}{' '}
      {sortField === field ? (sortAsc ? '↑' : '↓') : <span style={{ color: ct.legendHidden }}>↕</span>}
    </th>
  );

  const Th = ({ children, tip }: { children: React.ReactNode; tip?: string }) => (
    <th style={thStyle}>
      {children}
      {tip && (
        <Tooltip title={tip}>
          <InfoCircleOutlined style={{ marginInlineStart: 4, fontSize: 10, color: ct.mutedText, cursor: 'help' }} />
        </Tooltip>
      )}
    </th>
  );

  const Td = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' | 'center' }) => (
    <td
      style={{
        padding: '9px 12px',
        fontSize: 13,
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        textAlign: align,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </td>
  );

  return (
    <div>
      {/* Filters */}
      <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, marginBottom: 12 }}>
        <Select
          mode="multiple"
          size="small"
          placeholder={<AgentPlaceholder text={t('insights.pulse.filterByAgent')} />}
          value={selectedAgents}
          onChange={setSelectedAgents}
          options={agentOptions}
          maxTagCount={2}
          allowClear
          showSearch
          filterOption={agentFilterOption}
          style={AGENT_SELECT_STYLE}
          popupMatchSelectWidth={false}
          dropdownStyle={AGENT_DROPDOWN_STYLE}
          optionRender={renderAgentOption}
        />
        <Select
          mode="multiple"
          size="small"
          placeholder={<TenantPlaceholder text={t('insights.pulse.filterTenants')} />}
          value={selectedTenants}
          onChange={setSelectedTenants}
          options={tenantOptions}
          maxTagCount={2}
          allowClear
          showSearch
          optionFilterProp="label"
          style={TENANT_SELECT_STYLE}
          popupMatchSelectWidth={false}
          dropdownStyle={TENANT_DROPDOWN_STYLE}
          optionRender={(option) => renderTenantOption(option, tenantTree)}
        />
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', borderRadius: token.borderRadiusLG, border: `1px solid ${token.colorBorderSecondary}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1060 }}>
          <thead>
            <tr>
              <Th tip={t('insights.pulse.tipRank')}>{t('insights.pulse.rank')}</Th>
              <Th tip={t('insights.pulse.tipAgentId')}>{t('insights.pulse.agentId')}</Th>
              <Th>{t('insights.pulse.agentName')}</Th>
              <Th tip={t('insights.pulse.tipTenantWhere')}>{t('insights.pulse.tenant')}</Th>
              <SortHeader field="conversations">{t('insights.pulse.conversations')}</SortHeader>
              <SortHeader field="messages" tip={t('insights.pulse.tipMessages')}>{t('insights.pulse.messages')}</SortHeader>
              <SortHeader field="users">{t('insights.pulse.uniqueUsers')}</SortHeader>
              <SortHeader field="input_tokens" tip={t('insights.pulse.tipInputTokens')}>{t('insights.pulse.inputTokens')}</SortHeader>
              <SortHeader field="output_tokens" tip={t('insights.pulse.tipOutputTokens')}>{t('insights.pulse.outputTokens')}</SortHeader>
              <SortHeader field="cached_tokens" tip={t('insights.pulse.tipCachedTokens')}>{t('insights.pulse.cachedTokens')}</SortHeader>
              <Th tip={t('insights.pulse.tipTrend')}>{t('insights.pulse.trend')}</Th>
              <SortHeader field="delta_conversations" tip={t('insights.pulse.tipDeltaConvos')}>{t('insights.pulse.deltaConvos')}</SortHeader>
              <SortHeader field="delta_users" tip={t('insights.pulse.tipDeltaUsers')}>{t('insights.pulse.deltaUsers')}</SortHeader>
              <SortHeader field="delta_tokens" tip={t('insights.pulse.tipDeltaTokens')}>{t('insights.pulse.deltaTokens')}</SortHeader>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 14 }).map((_, j) => (
                    <td key={j} style={{ padding: '10px 12px', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
                      <Skeleton.Input active size="small" style={{ width: 60, height: 16, borderRadius: 4 }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={15} style={{ textAlign: 'center', padding: '40px 0', color: ct.mutedText, fontSize: 13 }}>
                  {t('insights.noAgentDataAvailable')}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={`${row.chatbot_id}-${row.tenant_name}`}
                  style={{ background: idx % 2 === 0 ? token.colorBgContainer : token.colorBgLayout }}
                >
                  <Td align="center"><RankBadge rank={idx + 1} bt={ct} /></Td>
                  <Td>
                    <Text type="secondary" copyable={{ text: row.chatbot_id }} style={{ fontSize: 11, fontFamily: 'monospace' }}>{row.chatbot_id.slice(0, 8)}</Text>
                  </Td>
                  <Td>
                    <Text strong style={{ fontSize: 13 }} dir="auto">{row.chatbot_name}</Text>
                  </Td>
                  <Td>
                    <Text type="secondary" style={{ fontSize: 12 }} dir="auto">{row.tenant_name}</Text>
                  </Td>
                  <Td align="center">
                    <Text style={{ fontWeight: 600 }}>{formatNumber(row.conversations)}</Text>
                  </Td>
                  <Td align="center">
                    <Text>{formatNumber(row.messages)}</Text>
                  </Td>
                  <Td align="center">
                    <Text>{formatNumber(row.users)}</Text>
                  </Td>
                  <Td align="center">
                    <Text>{formatNumber(row.input_tokens)}</Text>
                  </Td>
                  <Td align="center">
                    <Text>{formatNumber(row.output_tokens)}</Text>
                  </Td>
                  <Td align="center">
                    <Text>{formatNumber(row.cached_tokens)}</Text>
                  </Td>
                  <Td align="center">
                    <Sparkline data={row.sparklineData} color={PALETTE[idx % PALETTE.length]} />
                  </Td>
                  <Td align="center"><DeltaBadge pct={row.delta_conversations} bt={ct} /></Td>
                  <Td align="center"><DeltaBadge pct={row.delta_users} bt={ct} /></Td>
                  <Td align="center"><DeltaBadge pct={row.delta_tokens} bt={ct} /></Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
